import type { OpportunityStatusType } from '@/api/opportunity';
import { getPathWithParams } from '@/components/InstantSearch/util/url-search-params';
import type {
  ALL_OPPORTUNITY_SEARCH_PARAM_KEYS,
  OPPORTUNITY_ACTIVITY_SORT_KEYS,
  OpportunityActivitySearchParamType,
} from '@/router/searchParams/opportunitySearchParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const opportunities = {
  path: `${instance.path}/opportunities`,
  description:
    'Displays all opportunities imported from a CRM connection and metrics associated with them. This includes a breakdown of all opportunity status types such as open, pipeline, closed won, and closed lost.',
  navigable: true,
  pathParams: {},
  searchParams: {
    audience: {
      type: 'string',
      description: 'An audience ID to filter the opportunities by',
      requiresLookup: true,
      lookupType: 'audience',
    },
    'o.companyId': {
      type: 'string[]',
      description: 'A comma-separated string of company IDs',
      requiresLookup: true,
      lookupType: 'company',
    },
    'o.status': {
      type: 'enum',
      enumValues: ['PIPELINE', 'CLOSED_WON', 'CLOSED_LOST'] satisfies OpportunityStatusType[],
      description:
        'The status of the opportunity. Pipeline represents opportunities which were opened during the time frame. Closed-won and closed-lost represent opportunities which were closed during the time frame and either won or lost respectively.',
    },
    'o.amount': {
      type: 'range',
      description:
        'A range of amounts from min to max. Both are inclusive. At least one must be provided. Use square brackets. Example: [0,10] means 0 <= amount <= 10 or [10,] means amount >= 10',
    },
    'o.sortBy': {
      type: 'enum',
      enumValues: ['name', 'companyId', 'tld', 'openDate', 'closeDate', 'status', 'amount'],
      default: 'openDate',
      description: 'The field to sort by. Only one field can be sorted at a time.',
    } satisfies RouteSearchParamEnumType<OPPORTUNITY_ACTIVITY_SORT_KEYS>,
    'o.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'desc',
      description:
        'The sort order. Fields with type `number` or `date` are usually best sorted in descending order. While string fields are best sorted in ascending order.',
    },
    'o.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'o.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
    'o.search': {
      type: 'string',
      description: 'Text to search for in company name, opportunity name, or domain',
    },
    'o.vendorIdListPipelineInfluence': {
      type: 'string[]',
      description: 'A comma-separated string of vendor IDs which influenced pipeline opportunities',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListClosedwonInfluence': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced closed won opportunities',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListPipelineLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced pipeline opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListPipelineFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced pipeline opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListPipelineLastTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced pipeline opportunities with last touch attribution',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListClosedwonLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced closed won opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.vendorIdListClosedwonFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of vendor IDs which influenced closed won opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'o.campaignIdListPipelineInfluence': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced pipeline opportunities',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListClosedwonInfluence': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced closed won opportunities',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListPipelineLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced pipeline opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListPipelineFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced pipeline opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListPipelineLastTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced pipeline opportunities with last touch attribution',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListClosedwonLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced closed won opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignIdListClosedwonFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of campaign IDs which influenced closed won opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'o.campaignTypeIdListPipelineInfluence': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced pipeline opportunities',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListClosedwonInfluence': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced closed won opportunities',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListPipelineLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced pipeline opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListPipelineFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced pipeline opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListPipelineLastTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced pipeline opportunities with last touch attribution',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListClosedwonLinear': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced closed won opportunities with linear attribution',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
    'o.campaignTypeIdListClosedwonFirstTouch': {
      type: 'string[]',
      description:
        'A comma-separated string of offline event type IDs which influenced closed won opportunities via first touch attribution',
      requiresLookup: true,
      lookupType: 'campaignType',
    },
  },
} satisfies NavigableRouteType<ALL_OPPORTUNITY_SEARCH_PARAM_KEYS>;

export const opportunityDetail = {
  path: `${instance.path}/opportunities/:opportunity`,
  description: 'Displays metrics for an individual opportunity.',
  navigable: true,
  pathParams: {
    opportunity: {
      type: 'string',
      description: 'The ID of the opportunity the user wishes to view',
      requiresLookup: true,
      lookupType: 'opportunity',
      required: true,
    },
  },
  searchParams: {},
} satisfies NavigableRouteType;

export const opportunityById = (opportunityId?: string) =>
  opportunityId && `${opportunities.path}/${encodeURIComponent(opportunityId)}?oppType=pipeline`;

/**
 * Returns a URL string for the opportunity activity route with the given search params.
 *
 * @param params An object with keys that match the search params for the opportunity activity
 * route. Undefined values are skipped.
 * @returns A URL string with the search params appended as query params
 */
export const opportunityActivityWithParams = (
  params: Partial<OpportunityActivitySearchParamType>,
) => {
  return getPathWithParams(opportunities.path, params);
};
